import fetch from 'cross-fetch'
import { startOfWeek, endOfWeek, formatISO } from 'date-fns'
import BasketUpdateEvent from '@lib/basket-update-event'
import { spektrixApiUrl, fetchOptions } from './config'

const showEventsOrInstances = ({
  startFrom = startOfWeek(new Date()),
  startTo = endOfWeek(new Date()),
  type = 'events',
  prefix = 'instanceStart_'
}) => {
  return fetch(
    `${spektrixApiUrl}/${type}?${prefix}from=${formatISO(startFrom, {
      representation: 'date'
    })}&${prefix}to=${formatISO(startTo, { representation: 'date' })}`,
    {
      method: 'GET'
    }
  )
    .then(response => response.json())
    .then(data => {
      return data
    })
}

const showEvents = ({
  startFrom = startOfWeek(new Date()),
  startTo = endOfWeek(new Date())
}) => {
  return showEventsOrInstances({
    startFrom: startFrom,
    startTo: startTo,
    type: 'events'
  })
}

const showInstances = ({
  startFrom = startOfWeek(new Date()),
  startTo = endOfWeek(new Date())
}) => {
  return showEventsOrInstances({
    startFrom: startFrom,
    startTo: startTo,
    type: 'instances'
  })
}

const basket = () => {
  return fetch(`${spektrixApiUrl}/basket`, {
    method: 'GET',

    ...fetchOptions
  })
    .then(response => response.json())
    .then(data => {
      return data
    })
}

const updateBaskets = updates => {
  if (updates) {
  }
  const BasketUpdate = new Event(BasketUpdateEvent)
  window.dispatchEvent(BasketUpdate)
}

const addTicketsToBasket = async tickets => {
  const key = `${spektrixApiUrl}/basket/tickets`

  return new Promise((resolve, reject) => {
    fetch(key, {
      method: 'POST',
      body: JSON.stringify(tickets),
      ...fetchOptions
    })
      .then(response => {
        if (response.ok) {
          response.json().then(tickets => {
            updateBaskets()
            resolve()
          })
        } else {
          response.json().then(body => {
            reject(body)
          })
        }
      })
      .catch(error => {
        reject(error)
      })
  })
}

const addMerchItemToBasket = async merchItemId => {
  return new Promise((resolve, reject) => {
    fetch(`${spektrixApiUrl}/basket/merchandise`, {
      method: 'POST',
      body: JSON.stringify([{ stockItem: merchItemId }]),

      ...fetchOptions
    }).then(response => {
      if (response.ok) {
        updateBaskets()
        resolve()
      } else {
        response.json().then(body => reject(body))
      }
    })
  })
}

const removeMerchItemFromBasket = merchItemId => {
  return new Promise((resolve, reject) => {
    fetch(`${spektrixApiUrl}/basket/merchandise`, {
      ...fetchOptions
    }).then(response => {
      if (response.ok) {
        response.json().then(items => {
          const itemToRemove = items.find(
            item => item.stockItem.id === merchItemId
          )
          if (itemToRemove) {
            return fetch(
              `${spektrixApiUrl}/basket/merchandise/${itemToRemove.id}`,
              {
                method: 'DELETE',

                ...fetchOptions
              }
            ).then(response => {
              if (response.ok) {
                updateBaskets()
                resolve()
              } else {
                response.json().then(body => reject(body))
              }
            })
          } else {
            reject('Item not found in basket')
          }
        })
      } else {
        response.json().then(body => reject(body))
      }
    })
  })
}

const removeTicketsFromBasket = tickets => {
  return new Promise((resolve, reject) => {
    fetch(`${spektrixApiUrl}/basket/tickets?ticketIds=${tickets.join()}`, {
      method: 'DELETE',

      ...fetchOptions
    }).then(response => {
      if (response.ok) {
        updateBaskets()
        resolve()
      } else {
        response.json().then(body => reject(body))
      }
    })
  })
}

const removeTicketFromBasket = (basketId, ticketId) => {
  return fetch(`${spektrixApiUrl}/baskets/${basketId}/tickets/${ticketId}`, {
    method: 'DELETE',

    ...fetchOptions
  })
}

const addPromoCodeToBasket = code => {
  return fetch(`${spektrixApiUrl}/basket`, {
    method: 'PATCH',
    body: JSON.stringify({
      promoCode: code
    }),

    ...fetchOptions
  })
}

const addGiftVoucherToBasket = async giftVoucherData => {
  return new Promise((resolve, reject) => {
    // Ensure we're always sending an array
    const vouchersArray = Array.isArray(giftVoucherData)
      ? giftVoucherData
      : [giftVoucherData]

    fetch(`${spektrixApiUrl}/basket/gift-vouchers`, {
      method: 'POST',
      body: JSON.stringify(vouchersArray),
      ...fetchOptions
    })
      .then(response => {
        if (response.ok) {
          response.json().then(vouchers => {
            updateBaskets()
            resolve(vouchers)
          })
        } else {
          response.json().then(body => {
            reject(body)
          })
        }
      })
      .catch(error => {
        reject(error)
      })
  })
}

const updateGiftVoucherInBasket = async (voucherId, updates) => {
  return new Promise((resolve, reject) => {
    fetch(`${spektrixApiUrl}/basket/gift-vouchers/${voucherId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
      ...fetchOptions
    })
      .then(response => {
        if (response.ok) {
          response.json().then(voucher => {
            updateBaskets()
            resolve(voucher)
          })
        } else {
          response.json().then(body => {
            reject(body)
          })
        }
      })
      .catch(error => {
        reject(error)
      })
  })
}

const removeGiftVoucherFromBasket = async voucherId => {
  return new Promise((resolve, reject) => {
    fetch(`${spektrixApiUrl}/basket/gift-vouchers/${voucherId}`, {
      method: 'DELETE',
      ...fetchOptions
    })
      .then(response => {
        if (response.ok) {
          updateBaskets()
          resolve()
        } else {
          response.json().then(body => {
            reject(body)
          })
        }
      })
      .catch(error => {
        reject(error)
      })
  })
}

const addMembershipToBasket = async membershipId => {
  return new Promise((resolve, reject) => {
    fetch(`${spektrixApiUrl}/basket/membership-subscriptions`, {
      method: 'POST',
      body: JSON.stringify([{ membership: membershipId, autoRenew: true }]),
      ...fetchOptions
    })
      .then(response => {
        if (response.ok) {
          response.json().then(subscriptions => {
            updateBaskets()
            resolve(subscriptions)
          })
        } else {
          response.json().then(body => {
            reject(body)
          })
        }
      })
      .catch(error => {
        reject(error)
      })
  })
}

const updateMembershipInBasket = async (subscriptionId, updates) => {
  return new Promise((resolve, reject) => {
    fetch(
      `${spektrixApiUrl}/basket/membership-subscriptions/${subscriptionId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(updates),
        ...fetchOptions
      }
    )
      .then(response => {
        if (response.ok) {
          response.json().then(subscription => {
            updateBaskets()
            resolve(subscription)
          })
        } else {
          response.json().then(body => {
            reject(body)
          })
        }
      })
      .catch(error => {
        reject(error)
      })
  })
}

const removeMembershipFromBasket = async subscriptionId => {
  return new Promise((resolve, reject) => {
    fetch(
      `${spektrixApiUrl}/basket/membership-subscriptions/${subscriptionId}`,
      {
        method: 'DELETE',
        ...fetchOptions
      }
    )
      .then(response => {
        if (response.ok) {
          updateBaskets()
          resolve()
        } else {
          response.json().then(body => {
            reject(body)
          })
        }
      })
      .catch(error => {
        reject(error)
      })
  })
}

const addDonationToBasket = async donationData => {
  return new Promise((resolve, reject) => {
    // Ensure we're always sending an array
    const donationsArray = Array.isArray(donationData)
      ? donationData
      : [donationData]

    fetch(`${spektrixApiUrl}/basket/donations`, {
      method: 'POST',
      body: JSON.stringify(donationsArray),
      ...fetchOptions
    })
      .then(response => {
        if (response.ok) {
          response.json().then(donations => {
            updateBaskets()
            resolve(donations)
          })
        } else {
          response.json().then(body => {
            reject(body)
          })
        }
      })
      .catch(error => {
        reject(error)
      })
  })
}

const removeDonationFromBasket = async donationId => {
  return new Promise((resolve, reject) => {
    fetch(`${spektrixApiUrl}/basket/donations/${donationId}`, {
      method: 'DELETE',
      ...fetchOptions
    })
      .then(response => {
        if (response.ok) {
          updateBaskets()
          resolve()
        } else {
          response.json().then(body => {
            reject(body)
          })
        }
      })
      .catch(error => {
        reject(error)
      })
  })
}

export {
  spektrixApiUrl,
  addPromoCodeToBasket,
  addTicketsToBasket,
  addMerchItemToBasket,
  addGiftVoucherToBasket,
  updateGiftVoucherInBasket,
  removeGiftVoucherFromBasket,
  addMembershipToBasket,
  updateMembershipInBasket,
  removeMembershipFromBasket,
  addDonationToBasket,
  removeDonationFromBasket,
  basket,
  removeTicketFromBasket,
  removeTicketsFromBasket,
  removeMerchItemFromBasket,
  showEvents,
  showInstances,
  updateBaskets
}

// Export customer-related functions
export * from './customer'
